import React, { useMemo } from "react";
import { map } from "lodash";
import styled from "styled-components";
import { content } from "@glossy";
import { PeriodRange } from "@linkfluence/models/date";
import { useLocalStorage } from "@lkf/hooks";
import { natural } from "@tootools/sort";
import Toggle from "@ui-kit/form/toggle";
import Panel from "@ui-kit/popover/panel";
import Biglist from "@radarly/components/filterbar/panels/biglist";
import { useFilterbarChoices } from "@radarly/core/filters/use-cases";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { Clause } from "@radarly/models/clause";
import { isClauseEmpty } from "@radarly/models/clause/is";
import { refineClauseWithInnerClause } from "@radarly/models/clause/refiner";
import { transformClauseFromField } from "@radarly/models/clause/transformer";
import { FullFacet } from "@radarly/models/facets";
import { ClassifiedChoice } from "@radarly/models/filters";
import { Choice, Status } from "@radarly/models/filters";
import {
  getNextClauseFromStatusAndClassifiedValues,
  getNextClauseFromStatusAndValue,
  getNextClauseFromStatusAndValues,
} from "./clause";
import InnerPanel from "./inner-panel";

interface IProps {
  clause: Clause;
  facet: FullFacet;
  setClause: (clause: Clause) => void;
  hasTitle?: boolean;
  onApply: () => void;
  onCancel?: () => void;
  period: PeriodRange;
  isGlobalInsightPage?: boolean;
  validateLabel?: string;
  className?: string;
}

const Description = styled.div`
  margin-bottom: 24px;
  color: ${(props) => content(props, "secondary")};
`;

const StyledToggle = styled(Toggle)`
  margin-bottom: 24px;
`;

const RadarlyFilterbarPanelsBiglist = ({
  setClause,
  clause,
  facet,
  hasTitle,
  onApply,
  onCancel,
  period,
  validateLabel,
  className,
}: IProps) => {
  const project = useCurrentProject();
  const locale = useCurrentUserLocale();
  const [isSortedByWorkspace, setIsSortedByWorkspace] =
    useLocalStorage<boolean>("sort_filter_queries_by_workspaces", false);
  const { choices, orderedChoices, loading, error } = useFilterbarChoices(
    clause,
    facet,
    period
  );
  const baseChoices = natural(choices, "label");
  const classifiedChoices: ClassifiedChoice[] = useMemo(() => {
    if (choices && project.dashboards) {
      const processedChoices: (number | string)[] = [];
      const formatedChoices: ClassifiedChoice[] = [];
      project.dashboards.forEach((dashboard) => {
        const matches = choices.filter((choice) =>
          dashboard.focuses.some((focus) => focus.id === choice.id)
        );
        const workspaceChoices = natural(matches, "label");
        processedChoices.push(...workspaceChoices.map((wc) => wc.id));
        return formatedChoices.push({
          id: dashboard.label,
          choices: workspaceChoices,
        });
      });
      const missingChoices = choices.filter(
        (choice) => !processedChoices.includes(choice.id)
      );
      if (missingChoices.length > 0) {
        return natural(formatedChoices, "id").concat({
          id: tr(locale, "QUERIES_WITHOUT_WORKSPACE", {
            count: missingChoices.length,
          }),
          choices: missingChoices,
        });
      }
      return natural(formatedChoices, "id");
    }
    return [];
  }, [choices, locale, project.dashboards]);

  const onStatusChange = (choice: Choice, status: Status) => {
    setClause(
      getNextClauseFromStatusAndValue(clause, facet, choice.id, status)
    );
  };

  const onGlobalStatusChange = (status: Status, workspace?: string) => {
    if (isSortedByWorkspace && workspace) {
      const impactedChoices: Pick<Choice, "id" | "status">[] = [];
      const preservedChoices: Pick<Choice, "id" | "status">[] = [];
      classifiedChoices.forEach((classifiedChoice) => {
        if (classifiedChoice.id === workspace) {
          impactedChoices.push(
            ...classifiedChoice.choices.map(({ id, status }) => ({
              id,
              status,
            }))
          );
        } else {
          preservedChoices.push(
            ...classifiedChoice.choices.map(({ id, status }) => ({
              id,
              status,
            }))
          );
        }
      });
      const cleanPreservedChoices = preservedChoices.filter(
        (choice) =>
          !impactedChoices.some(
            (impactedChoice) => impactedChoice.id === choice.id
          )
      );
      return setClause(
        getNextClauseFromStatusAndClassifiedValues(
          clause,
          facet,
          impactedChoices,
          cleanPreservedChoices,
          status
        )
      );
    }
    setClause(
      getNextClauseFromStatusAndValues(
        clause,
        facet,
        map(choices, "id"),
        status
      )
    );
  };

  const onClear = () => {
    setClause(
      refineClauseWithInnerClause(
        clause,
        transformClauseFromField(
          {
            [facet.id]: [],
          },
          facet.custom ? [facet.id] : []
        )
      )
    );
  };

  return (
    <Panel
      locale={locale}
      onClear={onClear}
      hasTitle={hasTitle}
      title={tr(locale, "FILTERBAR::PANEL::FILTERBY", {
        filter: facet.label,
      })}
      size="m"
      onApply={onApply}
      onCancel={onCancel}
      disabled={onCancel ? isClauseEmpty(clause) : false}
      validateLabel={validateLabel}
      className={className}
      maxHeight={312}
    >
      {facet.description && <Description>{facet.description}</Description>}
      <InnerPanel loading={loading} error={error} locale={locale}>
        <Biglist
          choices={baseChoices}
          classifiedChoices={
            isSortedByWorkspace && project.dashboards && classifiedChoices
          }
          orderedChoices={orderedChoices}
          allowExclusion
          onSelect={onStatusChange}
          onGlobalSelect={onGlobalStatusChange}
          globalSelectors={
            isSortedByWorkspace ? null : ["include", "exclude", "none"]
          }
          popularChoices={null}
          placeholder={tr(
            locale,
            "FILTER_PANEL::SEARCH_PLACEHOLDER::" + facet.id
          )}
          locale={locale}
          toggleSort={
            choices?.length > 0 && (
              <StyledToggle
                label={tr(locale, "FILTER_PANEL::SORT_QUERIES_BY_WORKSPACE")}
                checked={isSortedByWorkspace}
                onChange={(_, value) => setIsSortedByWorkspace(value)}
              />
            )
          }
        />
      </InnerPanel>
    </Panel>
  );
};

export default RadarlyFilterbarPanelsBiglist;
